import type {
  CustomerAlias,
  CustomerAliasListResponse,
  CreateCustomerAliasRequest,
  UpdateCustomerAliasRequest,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

export class AliasApiError extends Error {
  constructor(
    public statusCode: number,
    public error: string,
    message: string
  ) {
    super(message);
    this.name = 'AliasApiError';
  }
}

export async function getCustomerAliases(
  customerId: string,
  signal?: AbortSignal
): Promise<CustomerAlias[]> {
  const response = await fetch(
    `${API_BASE_URL}/customers/${customerId}/aliases`,
    { method: 'GET', headers: { Accept: 'application/json' }, signal }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to fetch aliases: ${response.status}`,
    }));
    throw new AliasApiError(response.status, errorData.error, errorData.error);
  }
  const data: CustomerAliasListResponse = await response.json();
  return data.items;
}

export async function createCustomerAlias(
  customerId: string,
  payload: CreateCustomerAliasRequest,
  signal?: AbortSignal
): Promise<CustomerAlias> {
  const response = await fetch(
    `${API_BASE_URL}/customers/${customerId}/aliases`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal,
    }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to create alias: ${response.status}`,
    }));
    throw new AliasApiError(response.status, errorData.error, errorData.error);
  }
  const data = await response.json();
  return data.item;
}

export async function updateCustomerAlias(
  customerId: string,
  aliasId: string,
  payload: UpdateCustomerAliasRequest,
  signal?: AbortSignal
): Promise<CustomerAlias> {
  const response = await fetch(
    `${API_BASE_URL}/customers/${customerId}/aliases/${aliasId}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal,
    }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to update alias: ${response.status}`,
    }));
    throw new AliasApiError(response.status, errorData.error, errorData.error);
  }
  const data = await response.json();
  return data.item;
}

export async function deleteCustomerAlias(
  customerId: string,
  aliasId: string,
  signal?: AbortSignal
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/customers/${customerId}/aliases/${aliasId}`,
    { method: 'DELETE', signal }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to delete alias: ${response.status}`,
    }));
    throw new AliasApiError(response.status, errorData.error, errorData.error);
  }
}
